import { ContactEventLogs } from "@/components/features/contact/event-logs/ContactEventLogs";
import ContactNotesSection from "@/components/features/contact/ContactNotesSection/ContactNotesSection";
import {
  Card,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Typography,
} from "@/components/ui";
import type { Contact } from "@/lib/types";
import { useState } from "react";

type ContactDetailsSidebarProps = {
  contact: Contact;
  canViewNotes: boolean;
  canCreateNotes: boolean;
  canEditNotes: boolean;
  canDeleteNotes: boolean;
  canViewEventLog: boolean;
};

export const ContactDetailsSidebar = ({
  contact,
  canViewNotes,
  canCreateNotes,
  canEditNotes,
  canDeleteNotes,
  canViewEventLog,
}: ContactDetailsSidebarProps) => {
  const [tab, setTab] = useState<"notes" | "eventlog">("notes");

  return (
    <Card className="flex h-full min-h-0 flex-col overflow-hidden rounded-none border-none p-4">
      <Tabs
        value={tab}
        onValueChange={(value) => {
          setTab(value as "notes" | "eventlog");
        }}
        className="flex h-full min-h-0 flex-col"
      >
        <TabsList className="mb-2 w-full shrink-0 justify-start">
          <TabsTrigger value="notes" disabled={!canViewNotes}>
            Notes
          </TabsTrigger>
          <TabsTrigger value="eventlog" disabled={!canViewEventLog}>
            Event log
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="notes"
          className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <div className="flex min-h-0 flex-1 flex-col pr-2">
            <div className="bg-muted flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl p-4">
              {canViewNotes ? (
                <ContactNotesSection
                  className="h-full"
                  notes={contact.notes}
                  comments={contact.noteComments}
                  contactId={contact.id}
                  hasCreatePermission={canCreateNotes}
                  hasEditPermission={canEditNotes}
                  hasDeletePermission={canDeleteNotes}
                  hideLabel
                />
              ) : (
                <div className="flex h-full items-center justify-center px-4 text-center">
                  <Typography
                    variant="subHeading"
                    className="text-muted-foreground"
                  >
                    You do not have permission to view notes.
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        <TabsContent
          value="eventlog"
          className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <div className="flex min-h-0 flex-1 flex-col pr-2">
            {canViewEventLog ? (
              <ContactEventLogs events={contact.eventLogs} />
            ) : (
              <div className="bg-muted flex h-full items-center justify-center rounded-2xl p-4 text-center">
                <Typography
                  variant="subHeading"
                  className="text-muted-foreground"
                >
                  You do not have permission to view contact event logs.
                </Typography>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </Card>
  );
};
